import { useEffect, useState } from 'react';

import { PROCESSING_TIP_INTERVAL_MS } from '../../../processing';
import { Text } from '../../primitives/Text';
import { cn } from '../../utils/cn';
import type { ProcessingProTipContent } from './ProcessingStage.types';

/** The tip after `index`, wrapping — exported for the spec. */
export const nextProTipIndex = (index: number, count: number) =>
  count > 0 ? (index + 1) % count : 0;

/**
 * One pro tip under the processing screen's bar: "Pro tip: …", a new one every
 * {@link PROCESSING_TIP_INTERVAL_MS}, from a random start so a second wait
 * does not open on the same line. Outside the stage's live region: a tip
 * changing is not news a screen reader should announce.
 */
export function ProcessingProTip({
  label,
  tips,
  className,
}: ProcessingProTipContent & { className?: string }) {
  const [index, setIndex] = useState(() =>
    tips.length > 0 ? Math.floor(Math.random() * tips.length) : 0,
  );

  useEffect(() => {
    if (tips.length < 2) return;
    const timer = setInterval(
      () => setIndex((current) => nextProTipIndex(current, tips.length)),
      PROCESSING_TIP_INTERVAL_MS,
    );
    return () => clearInterval(timer);
  }, [tips.length]);

  const tip = tips[index % Math.max(tips.length, 1)];
  if (!tip) return null;
  return (
    <Text
      className={cn(
        'w-full text-center text-b2 font-medium text-body-600 native:font-mona-medium',
        className,
      )}>
      <Text className="text-b2 font-semibold text-subtitle-800 native:font-mona-semibold">
        {label}
      </Text>
      {`: ${tip}`}
    </Text>
  );
}
